export type ScopeInputSource = 'text' | 'voice' | 'preset';

export type ScopeContext =
  | 'room'
  | 'apartment'
  | 'bathroom'
  | 'balcony'
  | 'terrace'
  | 'driveway'
  | 'path'
  | 'parking'
  | 'paving'
  | 'other';

export type ScopeIntakeDraft = {
  rawText: string;
  source: ScopeInputSource;
  context: ScopeContext;
  contextLabel: string;
  needsContextConfirmation: boolean;
};

export const SCOPE_CONTEXT_LABELS: Record<ScopeContext, string> = {
  room: 'Pokój / odświeżenie wnętrza',
  apartment: 'Mieszkanie / lokal',
  bathroom: 'Łazienka',
  balcony: 'Balkon',
  terrace: 'Taras',
  driveway: 'Podjazd',
  path: 'Chodnik / ścieżka',
  parking: 'Parking / plac',
  paving: 'Bruk / nawierzchnia z kostki',
  other: 'Inny zakres',
};

/**
 * Scope Intake V1 rozpoznaje wyłącznie kontekst realizacji.
 * Nie wycenia, nie tworzy pozycji kosztorysu, nie nadaje stawek i nie zgaduje ilości.
 * Tekst i głos muszą przechodzić przez ten sam kontrakt wejściowy.
 */
export function buildScopeIntake(rawText: string, source: ScopeInputSource): ScopeIntakeDraft {
  const trimmed = rawText.trim();
  const context = detectScopeContext(trimmed);

  return {
    rawText: trimmed,
    source,
    context,
    contextLabel: SCOPE_CONTEXT_LABELS[context],
    needsContextConfirmation: context === 'other' || context === 'paving',
  };
}

export function detectScopeContext(input: string): ScopeContext {
  const text = normalize(input);

  if (!text) return 'other';

  if (hasAny(text, ['lazienka', 'lazience', 'lazienki'])) return 'bathroom';
  if (hasAny(text, ['balkon', 'balkonie', 'balkonu'])) return 'balcony';
  if (hasAny(text, ['taras', 'tarasie', 'tarasu'])) return 'terrace';
  if (hasAny(text, ['podjazd', 'podjezdzie', 'wjazd do garazu'])) return 'driveway';
  if (hasAny(text, ['chodnik', 'sciezka', 'dojscie do domu'])) return 'path';
  if (hasAny(text, ['parking', 'plac parkingowy', 'plac manewrowy'])) return 'parking';
  if (hasAny(text, ['mieszkanie', 'lokal', 'cale mieszkanie', 'caly lokal'])) return 'apartment';
  if (hasAny(text, ['pokoj', 'sypialnia', 'salon', 'odswiezenie pokoju', 'odswiezenie salonu'])) {
    return 'room';
  }

  if (hasAny(text, ['bruk', 'kostka', 'kostki', 'kostke', 'nawierzchnia z kostki'])) {
    return 'paving';
  }

  return 'other';
}

function hasAny(text: string, values: string[]): boolean {
  return values.some((value) => text.includes(value));
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[łŁ]/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
